import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import { VideoCredits } from '../../../../redux/api/fymAPI.types';
import { CreditBlockView } from './creditBlockView';
import { creditMessages } from './creditMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the credit block; each part uses its own slice of it */
import styles from './StudioCreditBlock.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface CreditHaveCellProps {
  credits: VideoCredits;
  view: CreditBlockView;
}

/**
 * "You have" of the credit block (design release-kit t5): ONE number, the plan credits left plus
 * the pack credits. The line under it says what this make does to it: "After this: N", "Not
 * touched" (an image), or the reset day when the balance is too low. The number turns red when
 * the balance is too low. A plan with no limit shows "No limit".
 */
const CreditHaveCellComponent: React.FC<CreditHaveCellProps> = ({ credits, view }) => {
  const intl = useIntl();

  if (view.left === null) {
    return (
      <div className={styles.cell}>
        <span className={styles.key}>{intl.formatMessage(creditMessages.blockHaveTitle)}</span>
        <span className={clsx(styles.value, styles.valueGood)} data-testid="credit-block-have">
          {intl.formatMessage(creditMessages.blockNoLimit)}
        </span>
        <span className={styles.sub}>
          {intl.formatMessage(creditMessages.blockUsedThisMonth, { count: credits.used })}
        </span>
      </div>
    );
  }

  const sub = view.isShort
    ? intl.formatMessage(creditMessages.blockResetsSub, {
        date: intl.formatDate(credits.resetsAt, { day: 'numeric', month: 'short' }),
      })
    : view.afterThis !== null
      ? intl.formatMessage(creditMessages.blockAfterThis, { count: view.afterThis })
      : intl.formatMessage(creditMessages.blockNotTouched);

  return (
    <div className={styles.cell}>
      <span className={styles.key}>{intl.formatMessage(creditMessages.blockHaveTitle)}</span>
      <span className={clsx(styles.value, view.isShort && styles.valueLow)}>
        <span data-testid="credit-block-have">{intl.formatNumber(view.left)}</span>
        <small className={styles.unit}>{intl.formatMessage(creditMessages.blockLeftUnit)}</small>
      </span>
      <span className={styles.sub} data-testid="credit-block-have-sub">
        {sub}
      </span>
    </div>
  );
};

export const CreditHaveCell = TypedMemo(CreditHaveCellComponent);
